import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, RefreshCw, Image as ImageIcon, Lock, MessageSquare, FileText, Loader2, Calendar, ChevronLeft, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../store';

const API_URL = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? ((window.location.hostname === 'devapp.soviewfx.fr' || window.location.hostname === 'devadmin.soviewfx.fr') ? 'https://devhub.soviewfx.fr' : ((window.location.hostname === 'app.soviewfx.fr' || window.location.hostname === 'admin.soviewfx.fr') ? 'https://hub.soviewfx.fr' : '')) : '');

function ParamRow({ label, value }) {
  if (value === null || value === undefined) return null;

  if (typeof value === 'object' && !Array.isArray(value)) {
    const entries = Object.entries(value);
    if (entries.length === 0) return null;
    return (
      <div className="space-y-1">
        <span className="text-xs text-muted-foreground capitalize">{label}</span>
        <div className="pl-3 border-l border-white/10 space-y-1">
          {entries.map(([k, v]) => (
            <ParamRow key={k} label={k} value={v} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-between items-start gap-4 py-1.5 border-b border-white/5 last:border-0">
      <span className="text-xs text-muted-foreground capitalize shrink-0">{label.replace(/_/g, ' ')}</span>
      <span className="text-xs text-white text-right font-medium break-all max-w-[60%]">
        {Array.isArray(value) ? value.join(', ') : String(value)}
      </span>
    </div>
  );
}

export default function ShareDetailPage() {
  const navigate = useNavigate();
  const { shareId } = useParams();
  const { user, token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [share, setShare] = useState(null);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/home'); return; }
  }, [user]);

  useEffect(() => {
    if (user && shareId) fetchShare();
  }, [user, shareId]);

  const fetchShare = async () => {
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const res = await fetch(`${API_URL}/api/share/${shareId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 404) {
        setNotFound(true);
        return;
      }
      if (!res.ok) throw new Error('Erreur lors du chargement');
      const data = await res.json();
      setShare(data);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const res = await fetch(`${API_URL}/api/share/${shareId}/resend`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Échec du renvoi');
      setShare(prev => ({
        ...prev,
        resend_count: (prev.resend_count || 0) + 1,
        last_resend_at: new Date().toISOString()
      }));
      toast.success('Simulation renvoyée avec succès !');
    } catch (err) {
      toast.error(err.message || 'Erreur lors du renvoi');
    } finally {
      setResending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    if (status === 'sent' || status === 'success') {
      return <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 font-medium">Envoyé</span>;
    }
    if (status === 'failed') {
      return <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 font-medium">Échec</span>;
    }
    return <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 font-medium">En attente</span>;
  };

  // Loading state
  if (loading) {
    return (
      <div className="h-full flex flex-col bg-background">
        <header className="h-14 glass flex items-center px-4 gap-4 flex-shrink-0">
          <button onClick={() => navigate('/share-history')} className="p-2 rounded-lg hover:bg-white/5">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-display font-bold text-lg uppercase tracking-wide">Détail du partage</h1>
        </header>
        <div className="flex-1 flex items-center justify-center">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  // Not found state
  if (notFound) {
    return (
      <div className="h-full flex flex-col bg-background">
        <header className="h-14 glass flex items-center px-4 gap-4 flex-shrink-0">
          <button onClick={() => navigate('/share-history')} className="p-2 rounded-lg hover:bg-white/5">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-display font-bold text-lg uppercase tracking-wide">Détail du partage</h1>
        </header>
        <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground p-4">
          <FileText size={48} className="mb-3 opacity-30" />
          <p className="text-sm text-white font-medium mb-1">Partage introuvable</p>
          <p className="text-xs mb-4">Ce partage n'existe pas ou a été supprimé.</p>
          <button onClick={() => navigate('/share-history')} className="btn-secondary text-xs">
            Retour à l'historique
          </button>
        </div>
      </div>
    );
  }

  // Error state (without data)
  if (error && !share) {
    return (
      <div className="h-full flex flex-col bg-background">
        <header className="h-14 glass flex items-center px-4 gap-4 flex-shrink-0">
          <button onClick={() => navigate('/share-history')} className="p-2 rounded-lg hover:bg-white/5">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-display font-bold text-lg uppercase tracking-wide">Détail du partage</h1>
        </header>
        <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground p-4">
          <AlertTriangle size={40} className="mb-2 opacity-30 text-red-400" />
          <p className="text-sm text-red-400 mb-3">{error}</p>
          <button onClick={fetchShare} className="btn-secondary text-xs">
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Loaded state
  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <header className="h-14 glass flex items-center px-4 gap-4 flex-shrink-0">
        <button onClick={() => navigate('/share-history')} className="p-2 rounded-lg hover:bg-white/5">
          <ArrowLeft size={20} />
        </button>
        <h1 className="font-display font-bold text-lg uppercase tracking-wide">Détail du partage</h1>
      </header>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex-1 overflow-y-auto p-4 space-y-4 pb-safe"
      >
        {/* 1. Images section */}
        {(share.simulated_image_url || share.original_image_url) && (
          <div className="card p-4">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Images</h3>
            <div className="grid grid-cols-2 gap-3">
              {share.simulated_image_url && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5">Simulation</p>
                  <img
                    src={share.simulated_image_url}
                    alt="Simulation"
                    className="w-full rounded-xl border border-white/10"
                  />
                </div>
              )}
              {share.original_image_url && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5">Original</p>
                  <img
                    src={share.original_image_url}
                    alt="Original"
                    className="w-full rounded-xl border border-white/10"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* 2. Info section */}
        <div className="card p-4">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Informations</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Mail size={14} className="text-muted-foreground shrink-0" />
              <span className="text-xs text-muted-foreground">Destinataire :</span>
              <span className="text-sm text-white">{share.recipient_email || share.email || '—'}</span>
            </div>
            <div className="flex items-center gap-2">
              <FileText size={14} className="text-muted-foreground shrink-0" />
              <span className="text-xs text-muted-foreground">Projet :</span>
              <span className="text-sm text-white font-medium">{share.project_name || 'Projet sans nom'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar size={14} className="text-muted-foreground shrink-0" />
              <span className="text-xs text-muted-foreground">Date d'envoi :</span>
              <span className="text-sm text-white">{formatDate(share.created_at)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Statut :</span>
              {getStatusBadge(share.status)}
            </div>
            <div className="flex items-center gap-2">
              <RefreshCw size={14} className="text-muted-foreground shrink-0" />
              <span className="text-xs text-muted-foreground">Nombre de renvois :</span>
              <span className="text-sm text-white">{share.resend_count || 0}</span>
            </div>
            {share.last_resend_at && (
              <div className="flex items-center gap-2">
                <Calendar size={14} className="text-muted-foreground shrink-0" />
                <span className="text-xs text-muted-foreground">Dernier renvoi :</span>
                <span className="text-sm text-white">{formatDate(share.last_resend_at)}</span>
              </div>
            )}
          </div>
        </div>

        {/* 3. Message section */}
        {share.message && (
          <div className="card p-4">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <MessageSquare size={12} /> Message personnalisé
            </h3>
            <div className="bg-white/5 rounded-xl p-3 border-l-2 border-primary">
              <p className="text-sm text-white whitespace-pre-wrap">{share.message}</p>
            </div>
          </div>
        )}

        {/* 4. Internal notes section */}
        {share.internal_notes && (
          <div className="card p-4">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Lock size={12} /> Notes internes
            </h3>
            <div className="bg-yellow-500/5 rounded-xl p-3 border border-yellow-500/20">
              <p className="text-sm text-white/80 whitespace-pre-wrap">{share.internal_notes}</p>
            </div>
          </div>
        )}

        {/* 5. Email content section */}
        {(share.email_subject || share.email_body) && (
          <div className="card p-4">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Contenu de l'email</h3>
            <div className="space-y-3">
              {share.email_subject && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Sujet</p>
                  <p className="text-sm text-white bg-white/5 rounded-lg px-3 py-2">{share.email_subject}</p>
                </div>
              )}
              {share.email_body && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Corps</p>
                  <pre className="text-sm text-white/80 bg-white/5 rounded-lg px-3 py-2 whitespace-pre-wrap font-body leading-relaxed">{share.email_body}</pre>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 6. Simulation parameters section */}
        {share.simulation_params && Object.keys(share.simulation_params).length > 0 && (
          <div className="card p-4">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Paramètres de simulation</h3>
            <div className="divide-y divide-white/5">
              {Object.entries(share.simulation_params).map(([key, value]) => (
                <ParamRow key={key} label={key} value={value} />
              ))}
            </div>
          </div>
        )}

        {/* 7. Actions section */}
        <div className="card p-4">
          <div className="flex gap-3">
            <button
              onClick={handleResend}
              disabled={resending}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-primary/10 text-primary hover:bg-primary/20 transition-all font-medium text-sm disabled:opacity-50"
            >
              {resending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <RefreshCw size={16} />
              )}
              Renvoyer
            </button>
            <button
              onClick={() => navigate('/share-history')}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-white/5 text-muted-foreground hover:text-white hover:bg-white/10 transition-all font-medium text-sm"
            >
              <ChevronLeft size={16} />
              Retour à l'historique
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
